"use strict";
/**
 * OPENING STOCK sheet rules (owner, 2026-10-30). The pharmacy loads the stock it already holds on the day the system starts being used —
 * one line per batch — from a sheet it PASTES (tab-separated, straight from Excel) or loads as a CSV. Every cell arrives as the TEXT the
 * clerk typed and is parsed HERE, by the one set of rules both the server (authoritative) and the screen (preview) use, so a row can never
 * mean one thing on the screen and another on the server.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_OPENING_QUANTITY = exports.MAX_PAISE = exports.OPENING_STOCK_MAX_ROWS = exports.OPENING_STOCK_REVERSAL_SOURCE_TYPE = exports.OPENING_STOCK_SOURCE_TYPE = void 0;
exports.openingStockReference = openingStockReference;
exports.parseOpeningQuantity = parseOpeningQuantity;
exports.parseRupeesToPaise = parseRupeesToPaise;
exports.parseGstPercent = parseGstPercent;
exports.parseYesNo = parseYesNo;
exports.parseOpeningDate = parseOpeningDate;
/** The `sourceEntityType` every opening-stock ledger entry carries — how an opening batch is told from one a receipt or a transfer made. */
exports.OPENING_STOCK_SOURCE_TYPE = 'OpeningStock';
/** The `sourceEntityType` of the ledger OUT entry that takes a whole opening-stock load back out (same load id as the load itself). */
exports.OPENING_STOCK_REVERSAL_SOURCE_TYPE = 'OpeningStockReversal';
exports.OPENING_STOCK_MAX_ROWS = 2000;
/** `StockBatch.unitCost` / `mrp` are 32-bit integers (paise): stay clear of the overflow. */
exports.MAX_PAISE = 2_000_000_000;
exports.MAX_OPENING_QUANTITY = 9_999_999;
/** The reference shown for one load, e.g. OPEN-3F9A1C2B (the first 8 characters of its id). */
function openingStockReference(runId) {
    return `OPEN-${runId.replace(/-/g, '').slice(0, 8).toUpperCase()}`;
}
/** A quantity in issue units: a positive number, up to 3 decimals ("1,200" and "12.5" are fine). null = not a usable quantity. */
function parseOpeningQuantity(text) {
    const cleaned = text.trim().replace(/,/g, '');
    if (!/^\d+(\.\d{1,3})?$/.test(cleaned))
        return null;
    const value = Number(cleaned);
    return value > 0 && value <= exports.MAX_OPENING_QUANTITY ? value : null;
}
/** Rupees as typed ("12", "12.5", "1,250.75", "₹ 12", "Rs. 12") -> whole paise. null = not an amount (negative, 3 decimals, text). */
function parseRupeesToPaise(text) {
    const cleaned = text
        .trim()
        .replace(/^(₹|rs\.?|inr)\s*/i, '')
        .replace(/,/g, '')
        .replace(/\s+/g, '');
    const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(cleaned);
    if (!match)
        return null;
    const paise = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0') || '0');
    return paise <= exports.MAX_PAISE ? paise : null;
}
/** GST percent: 0-100, up to 2 decimals, an optional trailing %. null = not a percentage. */
function parseGstPercent(text) {
    const cleaned = text.trim().replace(/%$/, '').trim();
    if (!/^\d+(\.\d{1,2})?$/.test(cleaned))
        return null;
    const value = Number(cleaned);
    return value <= 100 ? value : null;
}
/** "" -> undefined (left empty on purpose); y/yes/true/1 -> true; n/no/false/0 -> false; anything else -> null (not understood). */
function parseYesNo(text) {
    const cleaned = text.trim().toLowerCase();
    if (!cleaned)
        return undefined;
    if (['y', 'yes', 'true', '1'].includes(cleaned))
        return true;
    if (['n', 'no', 'false', '0'].includes(cleaned))
        return false;
    return null;
}
function pad2(value) {
    return String(value).padStart(2, '0');
}
function isRealDate(year, month, day) {
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}
/**
 * A date as a pharmacy writes it, returned as YYYY-MM-DD:
 *   dd/mm/yyyy · dd-mm-yyyy · dd.mm.yyyy · yyyy-mm-dd       -> that day
 *   mm/yy · mm/yyyy · mm-yy · mm-yyyy (a strip's "EXP 08/27") -> the LAST day of that month (a medicine is good to the end of its month)
 * null = empty or not a real date.
 */
function parseOpeningDate(text) {
    const cleaned = text.trim();
    if (!cleaned)
        return null;
    let match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(cleaned);
    if (match) {
        const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
        return isRealDate(year, month, day) ? `${year}-${pad2(month)}-${pad2(day)}` : null;
    }
    match = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(cleaned);
    if (match) {
        const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
        return isRealDate(year, month, day) ? `${year}-${pad2(month)}-${pad2(day)}` : null;
    }
    match = /^(\d{1,2})[/-](\d{2}|\d{4})$/.exec(cleaned);
    if (match) {
        const month = Number(match[1]);
        const year = match[2].length === 2 ? 2000 + Number(match[2]) : Number(match[2]);
        if (month < 1 || month > 12)
            return null;
        const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
        return `${year}-${pad2(month)}-${pad2(last)}`;
    }
    return null;
}
//# sourceMappingURL=opening-stock.js.map